export type PlaybackMediaKind = "audio" | "video";

export interface PlaybackIssueDetails {
  reason: string;
  mediaKind: PlaybackMediaKind;
  src?: string;
  readyState?: number;
  networkState?: number;
  errorCode?: number;
  errorMessage?: string;
  errorName?: string;
}

const SILENT_WAV_DATA_URI =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQQAAAAAAA==";

let mediaPlaybackUnlocked = false;
let sharedUnlockAudio: HTMLAudioElement | null = null;

export const MEDIA_UNLOCK_REQUEST_EVENT = 'dental-avatar:media-unlock-request';

export function requestMediaElementUnlock(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MEDIA_UNLOCK_REQUEST_EVENT));
}

export function buildPlaybackIssue(
  reason: string,
  mediaKind: PlaybackMediaKind,
  mediaElement?: HTMLMediaElement | null,
  error?: unknown
): PlaybackIssueDetails {
  const mediaError = mediaElement?.error;
  const errorObject = error instanceof Error ? error : null;

  return {
    reason,
    mediaKind,
    src: mediaElement?.currentSrc || mediaElement?.getAttribute("src") || undefined,
    readyState: mediaElement?.readyState,
    networkState: mediaElement?.networkState,
    errorCode: mediaError?.code,
    errorMessage: mediaError?.message || errorObject?.message || String(error ?? ""),
    errorName: errorObject?.name,
  };
}

export function describePlaybackIssue(issue: PlaybackIssueDetails): string {
  if (issue.errorName === "NotAllowedError") {
    return "ブラウザが自動再生を止めました。";
  }

  switch (issue.reason) {
    case "audio-play-failed":
    case "response-video-play-failed":
    case "viseme-audio-play-failed":
    case "manual-retry-failed":
      return "再生を開始できませんでした。";
    case "response-start-timeout":
    case "viseme-audio-start-timeout":
      return "再生開始を待っています。";
    case "audio-error":
    case "response-video-error":
    case "viseme-audio-error":
      return "メディアの読み込みに失敗しました。";
    case "response-media-missing":
      return "再生対象を準備できませんでした。";
    case "viseme-audio-preload-timeout":
      return "音声の読み込みに時間がかかっています。";
    default:
      return "再生が一時停止しています。";
  }
}

export function hasMediaPlaybackStarted(mediaElement: HTMLMediaElement): boolean {
  return mediaElement.currentTime > 0.1 || !mediaElement.paused;
}

export function isMediaPlaybackUnlocked(): boolean {
  return mediaPlaybackUnlocked;
}

export function markMediaPlaybackUnlocked(): void {
  mediaPlaybackUnlocked = true;
}

export async function unlockMediaPlayback(mediaElements: Array<HTMLMediaElement | null | undefined> = []): Promise<void> {
  if (typeof window === "undefined") return;

  let unlocked = false;
  for (const mediaElement of mediaElements) {
    if (!mediaElement) continue;
    unlocked = (await unlockExistingMediaElement(mediaElement)) || unlocked;
  }

  try {
    const audio = getSharedUnlockAudio();
    audio.muted = true;
    audio.volume = 0;
    audio.src = SILENT_WAV_DATA_URI;
    await audio.play();
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
    unlocked = true;
  } catch {
    // ブラウザごとに unlock 可否が異なるため、失敗しても録音操作自体は止めない。
  }

  if (unlocked) {
    markMediaPlaybackUnlocked();
  }
}

async function unlockExistingMediaElement(mediaElement: HTMLMediaElement): Promise<boolean> {
  if (mediaElement instanceof HTMLVideoElement) {
    const hasSource = Boolean(mediaElement.currentSrc || mediaElement.getAttribute("src"));
    if (!hasSource) return false;

    const wasPaused = mediaElement.paused;
    const previousMuted = mediaElement.muted;
    const previousVolume = mediaElement.volume;

    try {
      mediaElement.muted = true;
      mediaElement.volume = 0;
      await mediaElement.play();
      if (wasPaused) mediaElement.pause();
      return true;
    } catch {
      return false;
    } finally {
      mediaElement.muted = previousMuted;
      mediaElement.volume = previousVolume;
    }
  }

  if (!(mediaElement instanceof HTMLAudioElement)) {
    return false;
  }

  const hasSource = Boolean(mediaElement.currentSrc || mediaElement.getAttribute("src"));
  if (!hasSource) return false;

  const previousMuted = mediaElement.muted;
  const previousVolume = mediaElement.volume;
  const wasPaused = mediaElement.paused;

  try {
    mediaElement.muted = true;
    mediaElement.volume = 0;
    await mediaElement.play();
    if (wasPaused) mediaElement.pause();
    return true;
  } catch {
    return false;
  } finally {
    mediaElement.muted = previousMuted;
    mediaElement.volume = previousVolume;
  }
}

function getSharedUnlockAudio(): HTMLAudioElement {
  if (!sharedUnlockAudio) {
    sharedUnlockAudio = new Audio();
  }
  return sharedUnlockAudio;
}
